import { IEventLoggingComponentProps } from '@AppModels/Logging';
import { useContactApproximateQuery, useContactFilterQuery, useGetOwnersByFilter } from '@Queries/index';
import * as Api from '@ViewModels';
import { FilterOperator, IAccount, IContactFilterCriteria, VmUtils } from '@ViewModels';
import { css } from 'aphrodite';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AdvancedTagFilter } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import { UserSessionViewModelKey } from '../../../../models/AppState';
import { AccountUserViewModel, CreateCampaignApprovalViewModel } from '../../../../viewmodels/AdminViewModels';
import { IOwnershipFilter } from '../../../../viewmodels/AppViewModels';
import { AdvancedFiltersContextProvider } from '../../../../web/components-v2/AdvancedFilters/AdvancedFiltersContext';
import { AdvancedFiltersLegacyUI } from '../../../../web/components/AdvancedFilters/AdvancedFiltersLegacyUI';
import { Button } from '../../../../web/components/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { ContactsTableActiveSearchesList } from '../../../../web/components/contacts/ContactsTableActiveSearchesList';
import { EmailGuideTag } from '../../../../web/components/email/guide/EmailGuideTag';
import { FabContext } from '../../../../web/components/FabContext';
import { FlyoutModal } from '../../../../web/components/FlyoutModal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { white } from '../../../../web/styles/colors';
import { BulkEmailApprovalContactsList } from '../../../components/email/bulkEmailApproval/BulkEmailApprovalContactsList';
import { BulkEmailApprovalUsersSelect } from '../../../components/email/bulkEmailApproval/BulkEmailApprovalUsersSelect';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IAdminUserSessionComponentProps {
	account?: IAccount;
	approval: CreateCampaignApprovalViewModel;
	onRequestNext?(): void;
}

const ConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Continue',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Close',
	},
];

const _BulkEmailApprovalRecipientsSelect: React.FC<IProps> = ({ approval, onRequestNext, userSession }) => {
	const [showingAdvancedTagFiltersModal, setShowingAdvancedTagFiltersModal] = React.useState<boolean>(false);
	const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
	const [selectedUsers, setSelectedUsers] = React.useState<Api.IUser[]>(
		approval.selectedUsers?.map(x => x.toJs()) ?? []
	);
	const [selectState, setSelectState] = React.useState<'all' | 'some' | 'none'>('none');

	const onCancelConfirmationModalRequestClose = (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		setShowConfirmation(false);

		if (!cancel && result.isCta) {
			onRequestNext?.();
		}
	};

	const onContinueClicked = () => {
		const { bulkEmailComposer } = approval;
		if (
			bulkEmailComposer.emailMessage.contactEmailApproximation?.hasEmail /
				bulkEmailComposer.emailMessage.contactEmailApproximation?.total <
			0.25
		) {
			setShowConfirmation(true);
		} else {
			onRequestNext?.();
		}
	};

	const onRenderTag = (tagFilter: AdvancedTagFilter, index: number, total: number, onRemove: () => void) => {
		return (
			<EmailGuideTag
				className={css(
					styleSheet.headerTag,
					tagFilter.type === FilterOperator.Not ? styleSheet.headerTagExcludeOp : null
				)}
				hideCheckIcon={true}
				isSelected={true}
				key={`${tagFilter.tag}-${index}-${total}`}
				value={tagFilter.tag}
			>
				{total > 1 && (
					<button className={css(styleSheet.tagTokenRemoveButton)} onClick={onRemoveTagButtonClicked(onRemove)}>
						<XIcon color={white} height={10} width={10} className={css(styleSheet.tagTokenRemoveButtonIcon)} />
					</button>
				)}
			</EmailGuideTag>
		);
	};

	const onRemoveTagButtonClicked = (remove: () => void) => (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
		remove();
	};

	const tagSearchCriteria = computed(() =>
		VmUtils.sortContactFilterCriteria(filterRequest?.filter?.criteria).searches.filter(x =>
			VmUtils.isTagSearchContactFilterCriteria(x)
		)
	);

	const showAdvancedTagFiltersModal = () => {
		setShowingAdvancedTagFiltersModal(true);
	};

	const onSearchesChanged = (searches: IContactFilterCriteria[]) => {
		updateFilterRequest(searches);
	};

	const impersonationContext: Api.IImpersonationContext = React.useMemo(
		() => ({
			account: approval.account,
			user: selectedUsers[0],
		}),
		[approval.account, selectedUsers]
	);

	const [filterRequest, setFilterRequest] = React.useState<Api.ContactFilterRequest>({
		filter: approval.contactsFilterRequest?.filter,
		ownershipFilter: {
			criteria: {
				property: Api.ContactFilterCriteriaProperty.All,
			},
		},
		groupByHousehold: false,
	});

	const emailApproximationQuery = useContactApproximateQuery({
		filterRequest,
		capabilities: [Api.ContactCapability.ReceiveEmail],
		emailSendCategory: Api.ContactEmailApproximation.GroupSend,
		impersonationContext,
	});

	const getContactsQuery = useContactFilterQuery({
		filterRequest,
		impersonationContext,
	});

	const updateFilterRequest = async (
		newContactFilterCriteria?: IContactFilterCriteria[] | null,
		newOwnershipFilter?: IOwnershipFilter,
		householdGrouping?: boolean
	) => {
		const newFilterRequest: Api.ContactFilterRequest = {
			filter: {
				...filterRequest.filter,
				criteria: [...(newContactFilterCriteria ?? filterRequest.filter?.criteria ?? [])],
			},
			ownershipFilter: newOwnershipFilter ?? filterRequest.ownershipFilter,
			groupByHousehold: householdGrouping ?? false,
		};
		setFilterRequest(newFilterRequest);
	};

	React.useEffect(() => {
		if (!filterRequest.filter?.criteria?.length) return;
		if (selectedUsers.length > 0) {
			approval.selectedUsers.clear();
			approval.selectedUsers.addAll(selectedUsers.map(x => new AccountUserViewModel(userSession, x)));
		}
		const newFilterRequest = {
			ownershipFilter: filterRequest.ownershipFilter,
			filter: filterRequest.filter,
			groupByHousehold: filterRequest.groupByHousehold,
		};
		approval.setContactFilterRequest(newFilterRequest);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterRequest]);

	const contacts = React.useMemo(
		() => getContactsQuery.data?.pages.map(page => page.values).flat() ?? [],
		[getContactsQuery.data?.pages]
	);

	const onAdvancedFiltersFlyoutSave = (
		filterCriteria: IContactFilterCriteria[],
		householdGrouping?: boolean,
		ownershipFilter?: IOwnershipFilter
	) => {
		setShowingAdvancedTagFiltersModal(false);

		updateFilterRequest(filterCriteria, ownershipFilter, householdGrouping);
	};

	const onAdvancedFiltersFlyoutCancel = () => {
		setShowingAdvancedTagFiltersModal(false);
	};
	const getMoreContacts = () => {
		getContactsQuery.fetchNextPage();
	};

	const emailApproximation = emailApproximationQuery.data ?? {
		hasEmail: 0,
		total: 0,
	};
	const getOwnersByFilterQuery = useGetOwnersByFilter({
		enabled: true,
		impersonationContext,
		filterRequest: filterRequest.filter,
	});

	const availableUsers = getOwnersByFilterQuery.data?.values ?? [];

	const onOwnershipFilterChanged = (filter: Api.IOwnershipFilter) => {
		if (
			filter.criteria.property === filterRequest.ownershipFilter?.criteria?.property &&
			filter.criteria?.value === filterRequest.ownershipFilter?.criteria?.value
		)
			return;
		updateFilterRequest(null, filter);
	};

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader fullscreenHeader='Schedule Email for Clients: Pick Recipients' />
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.headerLabel)}>Send emails to:</div>
				<ContactsTableActiveSearchesList
					advancedSearchButtonStyles={[styleSheet.tagTokensAdvancedSearchButton]}
					className={css(styleSheet.searchesContainer)}
					filterCriteria={filterRequest?.filter}
					hideLabel={true}
					onAdvancedTagSearchClicked={showAdvancedTagFiltersModal}
					onRenderTag={onRenderTag}
					onSearchesChanged={onSearchesChanged}
					searches={tagSearchCriteria.get()}
					tokenContainerStyles={[styleSheet.tokenContainer]}
					tokenPrefixStyles={[styleSheet.tagTokenPrefix]}
				/>
			</div>
			<div className={css(styleSheet.content)}>
				<div className={css(styleSheet.leftPanel)}>
					<BulkEmailApprovalUsersSelect
						impersonationContext={impersonationContext}
						styles={[styleSheet.usersColumn]}
						filterRequest={filterRequest}
						selectedUsers={selectedUsers}
						setSelectedUsers={setSelectedUsers}
						selectState={selectState}
						setSelectState={setSelectState}
						availableUsers={availableUsers}
					/>
				</div>
				<div className={css(styleSheet.rightPanel)}>
					<BulkEmailApprovalContactsList
						contacts={contacts}
						filterRequest={filterRequest}
						selectedUsers={selectedUsers}
						getMoreContacts={getMoreContacts}
						isBusy={getContactsQuery.isPending}
						emailApproximation={emailApproximation}
						onFilterChanged={onOwnershipFilterChanged}
						impersonationContext={impersonationContext}
					/>
				</div>
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showConfirmation,
					onRequestClose: onCancelConfirmationModalRequestClose,
				}}
				options={ConfirmationOptions}
				title='Less than 25% of recipients have emails. Are you sure you want to continue?'
			/>
			<FlyoutModal
				isOpen={showingAdvancedTagFiltersModal}
				useDefaultHeader={false}
				onRequestClose={onAdvancedFiltersFlyoutCancel}
			>
				<AdvancedFiltersContextProvider
					initialFilterCriteria={filterRequest?.filter?.criteria}
					initialOwnershipFilter={filterRequest?.ownershipFilter}
					onSave={onAdvancedFiltersFlyoutSave}
					refresh={showingAdvancedTagFiltersModal}
				>
					<AdvancedFiltersLegacyUI
						onDismissFlyout={onAdvancedFiltersFlyoutCancel}
						showKeyFactFilters
						showOwnedByOption
						showCustomFields
					/>
				</AdvancedFiltersContextProvider>
			</FlyoutModal>
			{showAdvancedTagFiltersModal ? <FabContext appearance={{ hidden: true }} /> : null}
			<div className={css(styleSheet.footer)}>
				<Button kind='primary' onClick={onContinueClicked}>
					Next: Create or pick email template
				</Button>
			</div>
		</div>
	);
};

const BulkEmailApprovalRecipientsSelectAsObserver = observer(_BulkEmailApprovalRecipientsSelect);
export const BulkEmailApprovalRecipientsSelect = inject(UserSessionViewModelKey)(
	BulkEmailApprovalRecipientsSelectAsObserver
);
